/** Verify Deployment drawer — one-page form to tick ABM ✓ / MDM ✓ /
 *  AppleCare+ ✓ against the customer playbook and call the verify
 *  engine.  Pre-fills required flags from the customer playbook so ops
 *  only see what the customer actually requires. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const CHECKS = [
  { key: "delivery_confirmed", label: "Delivery confirmed", always: true },
  { key: "acknowledged",       label: "Employee acknowledgement", playbook: "ack_required" },
  { key: "abm_verified",       label: "ABM / ADE assignment", playbook: "abm_required" },
  { key: "mdm_verified",       label: "MDM enrollment", playbook: "primary_mdm" },
  { key: "autopilot_verified", label: "Windows Autopilot", playbook: "autopilot_required" },
  { key: "applecare_verified", label: "AppleCare+ coverage", playbook: "applecare_required" },
  { key: "encryption_verified",label: "Disk encryption", playbook: "encryption_required" },
  { key: "asset_tag_applied",  label: "Asset tag applied", playbook: "asset_tag_required" },
];

export default function VerifyDrawer({ requestId, onClose, onVerified }) {
  const [ev, setEv] = useState({ delivery_confirmed: true, serial_match: true });
  const [assetId, setAssetId] = useState("");
  const [loading, setLoading] = useState(false);
  const [playbook, setPlaybook] = useState(null);
  const [req, setReq] = useState(null);

  useEffect(() => {
    if (!requestId) return;
    let cancel = false;
    api.get(`/deployments/timeline/${requestId}`).then(({ data }) => {
      if (cancel) return;
      setReq(data.request);
      // Fetch playbook via the customer's mc_id
      const mcId = data.request?.master_company_id;
      if (mcId) {
        api.get(`/customer-playbook/${mcId}/playbook`).then(({ data }) => {
          if (cancel) return;
          setPlaybook({
            abm_required: data.apple?.abm_enabled === "yes",
            primary_mdm: data.apple?.primary_mdm,
            autopilot_required: data.windows?.autopilot_enabled === "yes",
            applecare_required: data.apple?.applecare_plus === "yes",
            encryption_required: (data.security?.disk_encryption) === "required",
            asset_tag_required: data.deployment?.asset_tag_required,
            ack_required: data.deployment?.user_ack_required,
          });
        }).catch(() => setPlaybook({}));
      } else {
        setPlaybook({});
      }
    }).catch(() => toast.error("Failed to load request"));
    return () => { cancel = true; };
  }, [requestId]);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/deployments/verify", {
        request_id: requestId,
        asset_id: assetId || req?.asset_id || undefined,
        ...ev,
      });
      toast.success(`Verified · ${data.verdict.replace("_", " ").toLowerCase()}`);
      onVerified?.(data);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
    finally { setLoading(false); }
  };

  const requiredChecks = CHECKS.filter((c) => c.always || (playbook && playbook[c.playbook]));
  const optionalChecks = CHECKS.filter((c) => !c.always && (!playbook || !playbook[c.playbook]));

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} data-testid="verify-drawer-backdrop">
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[color:var(--st-surface)] shadow-2xl overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid="verify-drawer">
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-[color:var(--st-border)] bg-[color:var(--st-surface)]">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" style={{ color: "var(--st-accent)" }} strokeWidth={1.8} />
            <div>
              <div className="text-sm font-semibold">Verify deployment</div>
              <div className="text-[10px] text-[color:var(--st-text-subtle)]">Playbook v{playbook ? "?" : "…"} · {req?.customer}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="verify-drawer-close"
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]">
            <X className="h-4 w-4" strokeWidth={1.6} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {req && (
            <div className="st-panel p-3 text-[13px]">
              <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">Deployment</div>
              <div><b>{req.customer}</b> — {req.employee_email}</div>
              <div className="text-[11px] text-[color:var(--st-text-muted)]">{req.purpose}</div>
            </div>
          )}

          <div>
            <label className="text-[12px] block mb-1">Asset ID (if not already set)</label>
            <input value={assetId} onChange={(e) => setAssetId(e.target.value)}
                   placeholder={req?.asset_id || "asset-…"}
                   className="w-full px-3 py-2 text-[13px] rounded-md border border-[color:var(--st-border)]"
                   data-testid="verify-asset-id" />
          </div>

          {requiredChecks.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)] mb-2">
                Customer standard requires
              </div>
              <div className="space-y-1">
                {requiredChecks.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 p-2 rounded-md st-panel cursor-pointer"
                         data-testid={`verify-check-${c.key}`}>
                    <input type="checkbox" checked={!!ev[c.key]}
                           onChange={(e) => setEv({ ...ev, [c.key]: e.target.checked })} />
                    <span className="text-[13px]">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {optionalChecks.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)] mb-2">
                Optional evidence
              </div>
              <div className="space-y-1">
                {optionalChecks.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 p-2 rounded-md cursor-pointer">
                    <input type="checkbox" checked={!!ev[c.key]}
                           onChange={(e) => setEv({ ...ev, [c.key]: e.target.checked })} />
                    <span className="text-[13px] text-[color:var(--st-text-muted)]">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-[color:var(--st-border)] p-3 flex justify-end gap-2 bg-[color:var(--st-surface)]">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="verify-submit">
            {loading ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Verifying…</> : "Verify deployment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
